import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { signInWithGoogle } from '../services/auth.service';
import './SignupPage.css';
import { updateAuth } from '../store/slices/authSlice';
import { userService } from '../services/userService';
import { useDispatch, useSelector } from 'react-redux';
import {setSwipeStats} from '../store/slices/cacheSlice';
import Spinner from '../components/common/Spinner';
import useTracking from '../hooks/useTracking';

const SignupPage = ({ encounterFlow = false }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const tracking = useTracking();
  const authState = useSelector(state => state.auth);
  const encounterProfile = encounterFlow ? {
    name: authState.name,
    doB: authState.doB,
    gender: authState.gender,
    genderSearch: authState.genderSearch,
    LookingFor: authState.LookingFor,
    onboardingSource: 'rencontre'
  } : {};
  const validatePassword = (password) => {
    const minLength = 8;

    const errors = [];
    if (password.length < minLength) errors.push("Au moins 8 caractères");
    // if (!hasUpperCase) errors.push("Au moins une majuscule");
    // if (!hasLowerCase) errors.push("Au moins une minuscule");
    // if (!hasNumbers) errors.push("Au moins un chiffre");
    // if (!hasSpecialChar) errors.push("Au moins un caractère spécial");

    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Reset error when user types
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation du mot de passe
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        setError("Le mot de passe doit contenir : " + passwordErrors.join(", "));
        setLoading(false);
        return;
      }

      // Vérification que les mots de passe correspondent
      if (formData.password !== formData.confirmPassword) {
        setError("Les mots de passe ne correspondent pas");
        setLoading(false);
        return;
      }

      // Création du compte Firebase
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      // Redirection vers la page de bienvenue ou de complétion du profil
      // navigate('/auth/welcome-policy');
      const user = userCredential.user;

      // Vérifier si l'utilisateur existe par fbid
      let existingUser = null;
      try {
          existingUser = await userService.getUserByFbid(user.uid);
      } catch (error) {
          if (error.response?.status !== 404) {
              throw error;
          }
      }

      // Si pas trouvé par fbid, vérifier par email
      if (!existingUser && user.email) {
          try {
              existingUser = await userService.getUserByEmail(user.email);
          } catch (error) {
              if (error.response?.status !== 404) {
                  throw error;
              }
          }
      }
      if (!existingUser) {
          const attribution = tracking.getAttributionData();
          // Créer un nouvel utilisateur
          const userData = {
              uid: user.uid,
              email: user.email,
              name: encounterFlow ? authState.name : user.displayName,
              profilePic: user.photoURL ? {
                  photoUrl: user.photoURL,
                  public_id: null
              } : null, 
              createdAt: new Date().toISOString(),
              provider: 'email',
              level: 'free',
              status: "Inactive",
              invitations: 5,
              ref: tracking.getLastRef(),
              weeklyInvitations: [],
              dailySwipes: 0,
              lastSwipeDate: null,
              acceptedInvitations: [],
              ...encounterProfile,
              ...attribution
          };

          // Créer l'utilisateur sur le serveur backend
          const response = await userService.createUser(userData);
          // Mettre à jour Redux avec les données du nouvel utilisateur
          dispatch(updateAuth(response));
          if (encounterFlow) {
            tracking.trackFunnelEvent('registration_completed', 'inscription', { userUid: user.uid });
          }
          // Rediriger vers la page de configuration initiale
          navigate(encounterFlow ? '/rencontre/regles' : '/auth/welcome-policy');
      } else {
          // Utilisateur existant - mettre à jour Redux et rediriger
          dispatch(updateAuth(existingUser));
          dispatch(setSwipeStats({
              dailySwipes: existingUser.dailySwipes ? existingUser.dailySwipes : 0,
              lastSwipeDate: existingUser.lastSwipeDate ? existingUser.lastSwipeDate : null
          }))
          navigate(existingUser.status === 'PendingDeletion' ? '/settings' : '/home');
          window.location.reload();
      }
    } catch (error) {
      console.error("Erreur d'inscription:", error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError("Cette adresse email est déjà utilisée");
          break;
        case 'auth/invalid-email':
          setError("L'adresse email n'est pas valide");
          break;
        case 'auth/operation-not-allowed':
          setError("L'inscription par email n'est pas activée");
          break;
        case 'auth/weak-password':
          setError("Le mot de passe est trop faible");
          break;
        default:
          setError("Une erreur est survenue lors de l'inscription");
      }
    } finally {
      setLoading(false);
    }
  };

  // Inscription/connexion via Google : retrouve ou crée l'utilisateur (via userService.resolveSocialUser)
  // en conservant le tracking (ref de parrainage, cookies Facebook) comme pour l'inscription par email.
  const handleGoogleSignup = async () => {
    setError(null);
    setLoading(true);

    try {
      const result = await signInWithGoogle();
      const attribution = tracking.getAttributionData();
      const { user, isNew } = await userService.resolveSocialUser(result.user, {
        provider: result.providerId || 'google.com',
        ...encounterProfile,
        ...attribution
      });

      dispatch(updateAuth(user));

      if (isNew) {
        if (encounterFlow) {
          tracking.trackFunnelEvent('registration_completed', 'inscription', { userUid: user.uid });
        }
        navigate(encounterFlow ? '/rencontre/regles' : '/auth/welcome-policy');
      } else {
        dispatch(setSwipeStats({
          dailySwipes: user.dailySwipes ? user.dailySwipes : 0,
          lastSwipeDate: user.lastSwipeDate ? user.lastSwipeDate : null
        }));
        navigate(user.status === 'PendingDeletion' ? '/settings' : '/home');
        window.location.reload();
      }
    } catch (error) {
      console.error("Erreur d'inscription Google:", error);

      // L'utilisateur a simplement fermé la popup : pas d'erreur à afficher
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        // rien à faire
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        setError('Ce compte existe déjà avec une autre méthode de connexion');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Trop de tentatives échouées. Veuillez réessayer plus tard');
      } else {
        setError("Une erreur est survenue lors de l'inscription avec Google");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
             {loading && <Spinner />}
      <div className="logo-container">
        <img 
          src={process.env.PUBLIC_URL + '/assets/img/fulllogo_transparent_nobuffer.png'} 
          alt="Trakna Logo" 
          className="signup-logo"
        />
      </div>
      <button 
        className="close-button" 
        onClick={() => navigate(encounterFlow ? '/rencontre/profil' : '/')}
        aria-label="Fermer"
      >
        <i className="fas fa-times"></i>
      </button>
      <div className="signup-container">
        <h1>Inscription</h1>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Votre email"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Mot de passe (8 caractères minimum)</label>
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="mot de passe "
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <i className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirmez votre mot de passe"
                required
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                <i className={`fas ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
              </button>
            </div>
          </div>

          <button type="submit" className="signup-button" disabled={loading}>
            {loading ? 'Inscription en cours...' : "S'inscrire"}
          </button>
        </form>

        <div className="alternative-options">
          <button
            className="btn btn-google w-100"
            onClick={handleGoogleSignup}
            disabled={loading}
          >
            <i className="fab fa-google me-2"></i>
            {t('welcome.continueWithGoogle')}
          </button>

          <div className="login-link">
            Déjà membre ?{' '}
            <Link to={encounterFlow ? '/login?redirect=/rencontre/regles' : '/login'}>Se connecter</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
